import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useApiSend, useApiGet } from '../utils/httpClientTs';
import {
  checkUserByEmail,
  createUser,
  initiateUser,
} from '../endpoints/usersTs';
import {
  CheckUserInfo,
  UserCreateRequest,
  InitiateUserRequest,
} from '../types/users';
import ValidatedTextField from './ValidatedTextField';
import {
  emailValidator,
  nameValidator,
  phoneNumberValidator,
} from '../utils/validators';
import StateSelector from './StateSelector';
import CountrySelector from './CountrySelector';
import ChapterSelector from './ChapterSelector';
import DateTimePicker from './DateTimePicker';
import { getPostalInfo } from '../endpoints/lookupsTs';
import dayjs, { Dayjs } from 'dayjs';
import { Add, CheckCircle } from '@mui/icons-material';
import AlertItem from './AlertItem';
import MemberActionModal from './MemberActionModal';

interface UserCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (userId: string) => void;
}

type StepType =
  | 'email'
  | 'userFound'
  | 'userCreation'
  | 'initiation'
  | 'success';

const UserCreateModal: React.FC<UserCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();

  // Step management
  const [activeStep, setActiveStep] = useState<StepType>('email');
  const [userType, setUserType] = useState<'member' | 'non-member'>('member');

  // Email step
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [checkedUser, setCheckedUser] = useState<CheckUserInfo | null>(null);
  const [checkingUser, setCheckingUser] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  // User creation form
  const [firstName, setFirstName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastName, setLastName] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>('');
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [cellPhone, setCellPhone] = useState<string>('');
  const [cellPhoneError, setCellPhoneError] = useState<boolean>(false);
  const [address1, setAddress1] = useState<string>('');
  const [address1Error, setAddress1Error] = useState<boolean>(false);
  const [address2, setAddress2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [cityError, setCityError] = useState<boolean>(false);
  const [stateId, setStateId] = useState<string | null>(null);
  const [stateError, setStateError] = useState<boolean>(false);
  const [stateRequired, setStateRequired] = useState<boolean>(false);
  const [postalCode, setPostalCode] = useState<string>('');
  const [delayPostalCode, setDelayPostalCode] = useState<string>('');
  const [postalCodeError, setPostalCodeError] = useState<boolean>(false);
  const [countryId, setCountryId] = useState<string>('');
  const [countryError, setCountryError] = useState<boolean>(false);

  // Initiation step (for existing users becoming members)
  const [chapterId, setChapterId] = useState<string>('');
  const [chapterError, setChapterError] = useState<boolean>(false);
  const [initiationDate, setInitiationDate] = useState<Dayjs | null>(null);
  const [initiationDateError, setInitiationDateError] =
    useState<boolean>(false);

  // Success state
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);

  // MemberActionModal state (for existing members)
  const [memberActionModalOpen, setMemberActionModalOpen] =
    useState<boolean>(false);

  const [getPostalInfoActive, setGetPostalInfoActive] =
    useState<boolean>(false);

  const { data: postalInfo } = useApiGet(
    ['postal-info', postalCode, countryId],
    () => getPostalInfo(postalCode, countryId),
    {
      enabled: getPostalInfoActive,
      retry: 1,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPostalCode(delayPostalCode);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delayPostalCode]);

  useEffect(() => {
    if (postalCode.length > 4 && countryId.length > 0) {
      setGetPostalInfoActive(true);
    } else {
      setGetPostalInfoActive(false);
    }
  }, [postalCode, countryId]);

  useEffect(() => {
    if (postalInfo) {
      if (postalInfo.city) {
        setCity(postalInfo.city);
        if (postalInfo.city.length > 0) {
          setCityError(false);
        }
      }
      if (postalInfo.state_id) {
        setStateId(postalInfo.state_id);
        setStateRequired(true);
        if (postalInfo.state_id) {
          setStateError(false);
        }
      }
    }
  }, [postalInfo]);

  const canEditAddress = useMemo(() => {
    if (
      postalCode &&
      postalCode.length > 0 &&
      countryId &&
      countryId.length > 0
    ) {
      return true;
    }
    return false;
  }, [postalCode, countryId]);

  // Reset all state when modal closes
  const resetState = useCallback(() => {
    setActiveStep('email');
    setUserType('member');
    setEmail('');
    setEmailError(false);
    setCheckedUser(null);
    setCheckError(null);
    setFirstName('');
    setFirstNameError(false);
    setLastName('');
    setLastNameError(false);
    setPhone('');
    setPhoneError(false);
    setCellPhone('');
    setCellPhoneError(false);
    setAddress1('');
    setAddress1Error(false);
    setAddress2('');
    setCity('');
    setCityError(false);
    setStateId(null);
    setStateError(false);
    setStateRequired(false);
    setPostalCode('');
    setDelayPostalCode('');
    setPostalCodeError(false);
    setCountryId('');
    setCountryError(false);
    setChapterId('');
    setChapterError(false);
    setInitiationDate(null);
    setInitiationDateError(false);
    setCreatedUserId(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Check user by email
  const handleCheckEmail = useCallback(async () => {
    if (!email) {
      setEmailError(true);
      return;
    }

    const validationResult = emailValidator(email);
    // emailValidator returns false when valid, string when invalid
    if (validationResult !== false) {
      setEmailError(true);
      return;
    }

    // Clear error if email is valid
    setEmailError(false);
    setCheckingUser(true);
    setCheckError(null);

    try {
      const response = await checkUserByEmail(email);
      if (response.user) {
        setCheckedUser(response.user);
        setActiveStep('userFound');
      } else {
        setCheckedUser(null);
        setActiveStep('userCreation');
      }
    } catch (error: any) {
      setCheckError(
        error?.message || 'Failed to check user. Please try again.'
      );
    } finally {
      setCheckingUser(false);
    }
  }, [email]);

  // Handle using existing user
  const handleUseExistingUser = useCallback(() => {
    if (!checkedUser) return;

    if (userType === 'non-member') {
      // Navigate to user detail page using the ID from checkedUser
      if (checkedUser.id) {
        navigate(`/user/${checkedUser.id}`);
        handleClose();
      } else {
        setCheckError('User ID not found. Please contact support.');
      }
    } else {
      // Check if user is already a member
      if (checkedUser.is_member) {
        // Open MemberActionModal for transfer/reinstatement
        if (checkedUser.id) {
          setMemberActionModalOpen(true);
        } else {
          setCheckError('User ID not found. Please contact support.');
        }
      } else {
        // Show initiation step for non-members becoming members
        setActiveStep('initiation');
      }
    }
  }, [userType, checkedUser, navigate, handleClose]);

  // Create new user
  const { mutate: createUserMutation, isPending: isCreatingUser } = useApiSend(
    createUser,
    (data) => {
      const userId = data.message;
      setCreatedUserId(userId);
      if (onSuccess) {
        onSuccess(userId);
      }
      // Navigate to user page after creation
      navigate(`/user/${userId}`);
      handleClose();
    },
    (error: any) => {
      setCheckError(
        error?.message || 'Failed to create user. Please try again.'
      );
    }
  );

  // Initiate existing user
  const { mutate: initiateUserMutation, isPending: isInitiatingUser } =
    useApiSend(
      (data: { userId: string; request: InitiateUserRequest }) =>
        initiateUser(data.userId, data.request),
      (data) => {
        // After initiation, navigate to user page using the ID from checkedUser
        if (checkedUser?.id) {
          navigate(`/user/${checkedUser.id}`);
          handleClose();
        } else {
          setCheckError('Initiation successful, but user ID not found.');
        }
      },
      (error: any) => {
        setCheckError(
          error?.message || 'Failed to initiate user. Please try again.'
        );
      }
    );

  const handleCreateUser = useCallback(() => {
    let valid = true;

    // Validate required fields
    if (firstName === '') {
      setFirstNameError(true);
      valid = false;
    }
    if (lastName === '') {
      setLastNameError(true);
      valid = false;
    }
    if (address1 === '') {
      setAddress1Error(true);
      valid = false;
    }
    if (city === '') {
      setCityError(true);
      valid = false;
    }
    if (stateRequired && stateId === null) {
      setStateError(true);
      valid = false;
    }
    if (postalCode === '') {
      setPostalCodeError(true);
      valid = false;
    }
    if (countryId === '') {
      setCountryError(true);
      valid = false;
    }

    // Only require initiation date and chapter for members
    if (userType === 'member' && !initiationDate) {
      setInitiationDateError(true);
      valid = false;
    }
    if (userType === 'member' && !chapterId) {
      setChapterError(true);
      valid = false;
    }

    if (!valid) {
      return;
    }

    // Clean phone numbers
    let tempPhone = phone;
    if (phone !== '' && phone !== null) {
      tempPhone = tempPhone.replace(/\D/g, '');
    }
    let tempCellPhone = cellPhone;
    if (cellPhone !== '' && cellPhone !== null) {
      tempCellPhone = tempCellPhone.replace(/\D/g, '');
    }

    const data: UserCreateRequest = {
      user_type: userType,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: tempPhone || '',
      mobile_phone: tempCellPhone || null,
      address1: address1,
      address2: address2 || null,
      city: city,
      state: stateId || '',
      postal_code: postalCode,
      country: countryId,
    };

    // Only include member-specific fields for members
    if (userType === 'member' && initiationDate && chapterId) {
      data.local_chapter_id = chapterId;
      data.initiation_date = initiationDate.format('YYYY-MM-DD');
    }

    createUserMutation(data);
  }, [
    firstName,
    lastName,
    email,
    phone,
    cellPhone,
    address1,
    address2,
    city,
    stateRequired,
    stateId,
    postalCode,
    countryId,
    userType,
    initiationDate,
    chapterId,
    createUserMutation,
  ]);

  const handleInitiateExistingUser = useCallback(() => {
    if (!chapterId) {
      setChapterError(true);
      return;
    }
    if (!initiationDate) {
      setInitiationDateError(true);
      return;
    }

    if (!checkedUser) {
      setCheckError(
        'User information not found. Please go back and check the email again.'
      );
      return;
    }

    if (!checkedUser.id) {
      setCheckError(
        'User ID is missing. The user information may be incomplete. Please go back and check the email again, or contact support if this issue persists.'
      );
      return;
    }

    const request: InitiateUserRequest = {
      initiation_date: initiationDate.format('YYYY-MM-DD'),
      local_chapter_id: chapterId,
    };

    initiateUserMutation({ userId: checkedUser.id, request });
  }, [chapterId, initiationDate, checkedUser, initiateUserMutation]);

  const setValue = useCallback(
    (field: string, isValid: boolean, value: string) => {
      switch (field) {
        case 'firstName':
          setFirstName(value);
          if (value !== '' || isValid === false) {
            setFirstNameError(false);
          }
          break;
        case 'lastName':
          setLastName(value);
          if (value !== '' || isValid === false) {
            setLastNameError(false);
          }
          break;
        case 'phone':
          setPhone(value);
          if (isValid === false && value !== '') {
            setPhoneError(true);
          } else setPhoneError(false);
          break;
        case 'cellPhone':
          setCellPhone(value);
          if (isValid === false && value !== '') {
            setCellPhoneError(true);
          } else setCellPhoneError(false);
          break;
        case 'address1':
          setAddress1(value);
          if (value !== '' || isValid === false) {
            setAddress1Error(false);
          }
          break;
        case 'address2':
          setAddress2(value);
          break;
        case 'city':
          setCity(value);
          if (value !== '' || isValid === false) {
            setCityError(false);
          }
          break;
        case 'state':
          setStateId(value ? value : null);
          if (value !== null || isValid === false) {
            setStateError(false);
          }
          break;
        case 'postalCode':
          setDelayPostalCode(value);
          if (value !== '' || isValid === false) {
            setPostalCodeError(false);
          }
          break;
        default:
          break;
      }
    },
    []
  );

  const steps = ['Email & Type', 'User Info', 'Complete'];
  const getStepIndex = (step: StepType): number => {
    switch (step) {
      case 'email':
        return 0;
      case 'userFound':
      case 'userCreation':
        return 1;
      case 'initiation':
      case 'success':
        return 2;
      default:
        return 0;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 'email':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Enter Email and Select User Type
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <ValidatedTextField
                  validator={emailValidator}
                  label="Email"
                  value={email}
                  onChange={(isValid, value) => {
                    setEmail(value);
                    if (value !== '' || isValid === false) {
                      setEmailError(false);
                    }
                  }}
                  errorOverride={emailError ? 'Email is required' : null}
                  valueRequired={true}
                  disabled={checkingUser}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="user-type-label">User Type</InputLabel>
                  <Select
                    labelId="user-type-label"
                    id="user-type-select"
                    value={userType}
                    label="User Type"
                    onChange={(event) =>
                      setUserType(event.target.value as 'member' | 'non-member')
                    }
                    disabled={checkingUser}
                  >
                    <MenuItem value="non-member">Non-Member</MenuItem>
                    <MenuItem value="member">Member</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {checkError && (
                <Grid item xs={12}>
                  <AlertItem
                    severity="error"
                    description={checkError}
                    onClose={() => setCheckError(null)}
                    keepOpen={true}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 'userFound':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Found
            </Typography>
            {checkedUser && (
              <Card sx={{ mt: 2, mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {checkedUser.first_name} {checkedUser.last_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Chip
                        label={checkedUser.is_member ? 'Member' : 'Non-Member'}
                        color={checkedUser.is_member ? 'success' : 'default'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {checkedUser.last_chapter && (
                        <Chip
                          label={`Chapter: ${checkedUser.last_chapter}`}
                          color="primary"
                          size="small"
                        />
                      )}
                    </Grid>
                    {checkedUser.home_phone && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Home Phone:</strong> {checkedUser.home_phone}
                        </Typography>
                      </Grid>
                    )}
                    {checkedUser.cell_phone && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cell Phone:</strong> {checkedUser.cell_phone}
                        </Typography>
                      </Grid>
                    )}
                    {checkedUser.address && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Address:</strong>
                          <br />
                          {checkedUser.address.address_1}
                          {checkedUser.address.address_2 &&
                            `, ${checkedUser.address.address_2}`}
                          <br />
                          {checkedUser.address.city},{' '}
                          {checkedUser.address.state || ''}{' '}
                          {checkedUser.address.postal_code}
                          <br />
                          {checkedUser.address.country}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Would you like to use this user?
            </Typography>
            <Box sx={{ mb: 2 }}>
              <AlertItem
                severity="info"
                description={
                  <Typography variant="body2">
                    <strong>Note:</strong> If you proceed with this user, you
                    can update any information (including phone numbers and
                    address) on their profile page after creation. This helps
                    prevent duplicate user accounts.
                  </Typography>
                }
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
            {checkError && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={checkError}
                  onClose={() => setCheckError(null)}
                  keepOpen={true}
                />
              </Box>
            )}
          </Box>
        );

      case 'userCreation':
        return (
          <Box sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Create New {userType === 'member' ? 'Member' : 'User'}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={nameValidator}
                  label="First Name"
                  value={firstName}
                  onChange={(isValid, value) =>
                    setValue('firstName', isValid, value)
                  }
                  errorOverride={
                    firstNameError ? 'This field is required' : null
                  }
                  valueRequired={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={nameValidator}
                  label="Last Name"
                  value={lastName}
                  onChange={(isValid, value) =>
                    setValue('lastName', isValid, value)
                  }
                  errorOverride={
                    lastNameError ? 'This field is required' : null
                  }
                  valueRequired={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={emailValidator}
                  label="Email"
                  value={email}
                  onChange={(isValid, value) => {
                    setEmail(value);
                    if (value !== '' || isValid === false) {
                      setEmailError(false);
                    }
                  }}
                  errorOverride={emailError ? 'This field is required' : null}
                  valueRequired={true}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={phoneNumberValidator}
                  label="Phone"
                  value={phone}
                  onChange={(isValid, value) =>
                    setValue('phone', isValid, value)
                  }
                  errorOverride={phoneError ? 'Invalid phone number' : null}
                  valueRequired={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={phoneNumberValidator}
                  label="Cell Phone"
                  value={cellPhone}
                  onChange={(isValid, value) =>
                    setValue('cellPhone', isValid, value)
                  }
                  errorOverride={cellPhoneError ? 'Invalid phone number' : null}
                  valueRequired={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CountrySelector
                  handleChange={(value) => {
                    setCountryId(value ? value.id : '');
                    if (value !== null) {
                      setCountryError(false);
                    }
                  }}
                  value={countryId}
                  error={countryError}
                  errorMessage={countryError ? 'This field is required' : null}
                  size="small"
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={null}
                  label="Postal Code"
                  value={delayPostalCode}
                  onChange={(isValid, value) =>
                    setValue('postalCode', isValid, value)
                  }
                  errorOverride={
                    postalCodeError ? 'This field is required' : null
                  }
                  valueRequired={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={null}
                  label="Address 1"
                  value={address1}
                  onChange={(isValid, value) =>
                    setValue('address1', isValid, value)
                  }
                  errorOverride={
                    address1Error ? 'This field is required' : null
                  }
                  valueRequired={true}
                  disabled={!canEditAddress}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={null}
                  label="Address 2"
                  value={address2}
                  onChange={(isValid, value) =>
                    setValue('address2', isValid, value)
                  }
                  valueRequired={false}
                  disabled={!canEditAddress}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValidatedTextField
                  validator={null}
                  label="City"
                  value={city}
                  onChange={(isValid, value) =>
                    setValue('city', isValid, value)
                  }
                  errorOverride={cityError ? 'This field is required' : null}
                  valueRequired={true}
                  disabled={!canEditAddress}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StateSelector
                  countryId={countryId}
                  error={stateError}
                  errorMessage={stateError ? 'This field is required' : null}
                  size="small"
                  disabled={!canEditAddress}
                  required={true}
                  value={stateId}
                  handleChange={(value) =>
                    setValue('state', value === null, value?.id || '')
                  }
                />
              </Grid>
              {userType === 'member' && (
                <>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      date={initiationDate}
                      label="Initiation Date"
                      minDate={dayjs('2025-03-01')}
                      maxDate={dayjs('2026-03-01')}
                      handleDateChange={(date) => {
                        setInitiationDate(date);
                        if (date) {
                          setInitiationDateError(false);
                        }
                      }}
                      handleErrorUpdate={setInitiationDateError}
                      extraError={initiationDateError}
                      extraErrorMessage={
                        initiationDateError ? 'This field is required' : ''
                      }
                      disableFuture={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChapterSelector
                      chapter={chapterId}
                      spd={null}
                      reportId="user-create-modal"
                      handleChapterSelect={(value) => {
                        setChapterId(value ? value : '');
                        if (value !== null && value !== '') {
                          setChapterError(false);
                        }
                      }}
                    />
                    {chapterError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        This field is required
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
              {checkError && (
                <Grid item xs={12}>
                  <AlertItem
                    severity="error"
                    description={checkError}
                    onClose={() => setCheckError(null)}
                    keepOpen={true}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 'initiation':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Initiate Member
            </Typography>
            {checkedUser && (
              <Card sx={{ mt: 2, mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {checkedUser.first_name} {checkedUser.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {email}
                  </Typography>
                </CardContent>
              </Card>
            )}
            {(!checkedUser || !checkedUser.id) && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={
                    <Typography variant="body2">
                      <strong>Error:</strong> User information is missing.
                      Please go back and check the email again.
                    </Typography>
                  }
                  onClose={() => {}}
                  keepOpen={true}
                />
              </Box>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  date={initiationDate}
                  label="Initiation Date"
                  minDate={dayjs('2025-03-01')}
                  maxDate={dayjs('2026-03-01')}
                  handleDateChange={(date) => {
                    setInitiationDate(date);
                    if (date) {
                      setInitiationDateError(false);
                    }
                  }}
                  handleErrorUpdate={setInitiationDateError}
                  extraError={initiationDateError}
                  extraErrorMessage={
                    initiationDateError ? 'This field is required' : ''
                  }
                  disableFuture={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={chapterId}
                  spd={null}
                  reportId="user-create-modal-initiate"
                  handleChapterSelect={(value) => {
                    setChapterId(value ? value : '');
                    if (value !== null && value !== '') {
                      setChapterError(false);
                    }
                  }}
                />
                {chapterError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    This field is required
                  </Typography>
                )}
              </Grid>
              {checkError && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <AlertItem
                    severity="error"
                    description={
                      <Typography variant="body2">{checkError}</Typography>
                    }
                    onClose={() => setCheckError(null)}
                    keepOpen={true}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              User {userType === 'member' ? 'Initiated' : 'Created'}{' '}
              Successfully!
            </Typography>
            {createdUserId && (
              <Typography variant="body2" color="text.secondary">
                User ID: {createdUserId}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const handleBack = () => {
    if (activeStep === 'userFound' || activeStep === 'userCreation') {
      setActiveStep('email');
      setCheckError(null);
    } else if (activeStep === 'initiation') {
      setActiveStep('userFound');
      setCheckError(null);
    }
  };

  const handleNext = () => {
    if (activeStep === 'email') {
      handleCheckEmail();
    } else if (activeStep === 'userFound') {
      handleUseExistingUser();
    } else if (activeStep === 'userCreation') {
      handleCreateUser();
    } else if (activeStep === 'initiation') {
      handleInitiateExistingUser();
    }
  };

  const canGoBack = activeStep !== 'email' && activeStep !== 'success';
  const isNextDisabled =
    (activeStep === 'email' && (!email || checkingUser)) ||
    (activeStep === 'userFound' && !checkedUser) ||
    (activeStep === 'userCreation' && isCreatingUser) ||
    (activeStep === 'initiation' && isInitiatingUser);

  const getNextButtonText = () => {
    switch (activeStep) {
      case 'email':
        return 'Check Email';
      case 'userFound':
        if (userType === 'non-member') {
          return 'Use This User';
        } else if (checkedUser?.is_member) {
          return 'Transfer / Reinstate Member';
        } else {
          return 'Add Initiation Info';
        }
      case 'userCreation':
        return userType === 'member' ? 'Create & Initiate' : 'Create User';
      case 'initiation':
        return 'Initiate';
      default:
        return 'Next';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          {userType === 'member' ? 'Create/Initiate Member' : 'Create User'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={getStepIndex(activeStep)} sx={{ mb: 3, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent()}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          disabled={isCreatingUser || isInitiatingUser}
        >
          Cancel
        </Button>
        <Box>
          {canGoBack && (
            <Button
              onClick={handleBack}
              sx={{ mr: 1 }}
              disabled={isCreatingUser || isInitiatingUser}
            >
              Back
            </Button>
          )}
          {activeStep !== 'success' && (
            <LoadingButton
              onClick={handleNext}
              variant="contained"
              loading={checkingUser || isCreatingUser || isInitiatingUser}
              disabled={isNextDisabled}
              startIcon={
                activeStep === 'userCreation' || activeStep === 'initiation' ? (
                  <Add />
                ) : undefined
              }
            >
              {getNextButtonText()}
            </LoadingButton>
          )}
          {activeStep === 'success' && createdUserId && (
            <Button
              variant="contained"
              onClick={() => {
                navigate(`/user/${createdUserId}`);
                handleClose();
              }}
            >
              View User
            </Button>
          )}
        </Box>
      </DialogActions>
      {checkedUser?.is_member && checkedUser.id && (
        <MemberActionModal
          open={memberActionModalOpen}
          onClose={() => setMemberActionModalOpen(false)}
          userId={checkedUser.id}
          actionType="transfer"
          onSuccess={() => {
            setMemberActionModalOpen(false);
            if (checkedUser.id) {
              navigate(`/user/${checkedUser.id}`);
            }
            handleClose();
          }}
        />
      )}
    </Dialog>
  );
};

export default UserCreateModal;
